/**
 * Customer-facing API surface — the allowlist of routes that are safe to ship
 * inside customer-facing packages (the MCP server, the CLI, any public SDK).
 *
 * This lives in its own module (never alongside the full `apiRoutes`) so that a
 * consumer importing it never drags the admin / tester / internal route strings
 * into its bundle. `@runhuman/mcp-server` bundles this verbatim; keeping the
 * admin routes in a separate module is what keeps them out of the published npm
 * artifact (#5988) — module separation, not fragile intra-module tree-shaking.
 *
 * Fail-closed: a route reaches customer bundles ONLY by being listed here.
 * Adding one ships it publicly, so add deliberately.
 */
import { defineRoute } from './route-builder.js';

export const customerApiRoutes = {
  // Jobs
  /** List jobs (GET) or create job (POST) */
  jobs: defineRoute('/jobs'),

  /**
   * Re-run an existing job (POST). Clones the source job's settings verbatim and
   * re-dispatches them; the optional body overrides any field except the
   * system-owned project/org. Powers the dashboard Re-run button and the
   * `runhuman rerun` CLI command.
   */
  jobRerun: defineRoute('/jobs/:jobId/rerun'),

  /** Get job status (for polling) */
  jobStatus: defineRoute('/jobs/:jobId/status'),

  /** Get individual job artifact by type */
  jobArtifact: defineRoute('/jobs/:jobId/artifacts/:artifactType'),

  /**
   * Public capability redirect to an extracted video frame's freshly-signed S3
   * URL (#6010). The jobId UUID is the capability — no auth — so a filed
   * GitHub/Jira/Linear issue can embed a durable image URL. `frameId` is
   * `<sessionIndex>-<canonicalSecond>`.
   */
  jobFrame: defineRoute('/jobs/:jobId/frames/:frameId'),

  /** Status of the latest baked-demo-video render for a job (auth: dashboard). */
  bakedDemoVideoStatus: defineRoute('/jobs/:jobId/baked-demo-video-status'),

  /**
   * Trigger a baked-demo-video render against an already-completed job (#3837).
   * Idempotent: if a render is already queued/running/succeeded for the job,
   * the existing row's status is returned and no new row is enqueued. Only
   * enqueues when no row exists or the latest row is `failed`.
   */
  bakedDemoVideoRender: defineRoute('/jobs/:jobId/baked-demo-video'),

  // Projects, templates & schedules
  /** List projects (GET) or create project (POST) */
  projects: defineRoute('/projects'),

  /** List templates for a project (GET) or create template (POST) */
  projectTemplates: defineRoute('/projects/:projectId/templates'),

  /** Get/update/delete specific template */
  projectTemplate: defineRoute('/projects/:projectId/templates/:templateId'),

  /** List schedules for a project (GET) or create schedule (POST) */
  projectSchedules: defineRoute('/projects/:projectId/schedules'),

  /** Get/update/delete specific schedule */
  projectSchedule: defineRoute('/projects/:projectId/schedules/:scheduleId'),

  // Organizations
  /** List organizations (GET) or create organization (POST) */
  organizations: defineRoute('/organizations'),

  /** List organization members */
  organizationMembers: defineRoute('/organizations/:organizationId/members'),

  /** Invite member to organization (via Clerk) */
  organizationInvite: defineRoute('/organizations/:organizationId/invite'),

  /** List organization projects */
  organizationProjects: defineRoute('/organizations/:organizationId/projects'),

  // Tester notes (customer-authored guidance for testers)
  /** List/search tester notes (GET) or create note (POST) */
  testerNotes: defineRoute('/tester/notes'),

  /** Get/update a specific tester note */
  testerNote: defineRoute('/tester/notes/:noteId'),

  /** Archive a tester note (soft delete) */
  testerNoteArchive: defineRoute('/tester/notes/:noteId/archive'),
} as const;
